// traceLaneWeight.js — how much of a conversation's token mass each LANE is
// responsible for: a running total, plus the per-turn steps that built it.
//
// The density strip reads the timeline one way: at turn t, this much mass
// entered the context. This reads it the other way, per SUBJECT: of everything
// that entered, this file / host / tool put in this much. The lanes plane
// already answers "when was this touched"; a lane whose marks are three quiet
// reads and a lane whose marks are three 40k-line reads are indistinguishable
// there, and they are not the same thing at all to anyone asking where the
// context went.
//
// MASS, NEVER DOLLARS, and the choice is about what the numbers are made of.
// Mass is computed from the turns in front of us: it can only ever describe
// this conversation. The strip's cost tier is calibrated against a measured
// token rollup supplied by the caller, and that rollup answers at whatever
// grain the store had — per-turn, per-trace, or a total SHARED across a whole
// session or user-day (see tokenGrain.js). One hedged aggregate can carry that;
// a per-subject ranking with percentages invites much more specific conclusions
// than a shared rollup can support.
//
// ATTRIBUTION IS A PARTITION. Every block of every turn is charged to at most
// one set of lanes, and a block charged to N lanes is split N ways, so the
// per-lane shares of a turn sum to that turn's own mass rather than to some
// multiple of it. Three rules cover the trace:
//
//   tool_use     -> the lane(s) whose items are anchored at that block. Every
//                   tool_use has at least one: the extractors claim it for a
//                   subject, or it lands in a residual "Unclassified" lane.
//   tool_result  -> whoever owns the call it answers. This is where the mass
//                   actually is (a read's path is 40 chars, its body is 40k),
//                   so a lane that only ever appears as a tool_use anchor would
//                   otherwise read as weightless.
//   everything   -> the conversation's own lanes. A user or system turn's text
//   else          and images go to the prompt lane. An assistant turn's prose
//                 and thinking go to whichever conversation lane MARKS that
//                 turn — the narration lane where the turn was commentary, the
//                 reply lane where it was the episode's conclusion. The two are
//                 disjoint by construction (extractNarration skips reply turns),
//                 and charging both to `reply` credited it with the mass of
//                 every turn it has no mark on while commentary read as 0.
//
// What no rule claims (a tool_result whose call is not in the window) is
// reported as `unattributed` rather than spread around.
import { blockChars, estimateTurnTokens } from "./traceTurnDensity.js";

const PROMPT_LANE = "prompt:all";
const REPLY_LANE = "reply:all";
const NARRATION_LANE = "narration:all";

function addOwner(map, key, laneId) {
    let set = map.get(key);
    if (!set) {
        set = new Set();
        map.set(key, set);
    }
    set.add(laneId);
}

/**
 * Which lanes own each tool call, keyed both by block POSITION and by tool_use
 * id. Position is the authority — the semantic feed mints positional ids for
 * id-less calls on its own copy of the turns, so the id on the block a lane
 * item points at can be null where the feed had one. The id map exists only so
 * a tool_result can find its call's owner.
 */
function callOwners(turns, lanes) {
    const byPos = new Map();
    const byUseId = new Map();
    for (const lane of lanes) {
        for (const item of lane.items || []) {
            if (item.turnIndex == null) continue;
            const block = turns[item.turnIndex]?.blocks?.[item.blockIndex];
            if (block?.type !== "tool_use") continue;
            addOwner(byPos, `${item.turnIndex}:${item.blockIndex}`, lane.id);
            if (block.id) addOwner(byUseId, block.id, lane.id);
        }
    }
    return { byPos, byUseId };
}

/**
 * Build the per-lane weight model from a trace's turns and buildTraceLanes'
 * output. Self-contained: no measured totals, no rates, no catalog.
 *
 * Returns null when there is nothing to weigh. Otherwise:
 *   byLane:       Map(laneId -> { total, steps }) — steps ascending by turn,
 *                 one per turn the lane took mass on: { turn, est, cumEst }
 *   maxTotal:     the largest per-lane total — the shared normalizer, so a row
 *                 drawn against it is comparable to every other row
 *   attributed:   estimated tokens that landed on some lane
 *   unattributed: estimated tokens that landed on none
 *   whole:        attributed + unattributed, the denominator for a lane's share
 */
export function buildLaneWeights(turns, model) {
    const safeTurns = Array.isArray(turns) ? turns : [];
    const lanes = model?.lanes || [];
    if (!safeTurns.length || !lanes.length) return null;

    const { byPos, byUseId } = callOwners(safeTurns, lanes);
    const has = (id) => Boolean(model.laneById?.get?.(id));
    // Which turns the narration lane marks. Its own items would answer this
    // too, but they point at the PROSE block, and what this needs is the turn.
    const narratedTurns = new Set((model?.narration || []).map((n) => n.turnIndex));
    // The conversation lane that owns a turn's non-tool blocks. An assistant
    // turn's prose belongs to the lane that marks it; the reply lane stays the
    // fallback so an assistant turn carrying only an image — which no note is
    // ever minted for — keeps landing somewhere instead of going unattributed.
    const roleLane = (role, turnIndex) => {
        if (role !== "assistant") return has(PROMPT_LANE) ? PROMPT_LANE : null;
        if (narratedTurns.has(turnIndex) && has(NARRATION_LANE)) return NARRATION_LANE;
        return has(REPLY_LANE) ? REPLY_LANE : null;
    };

    const byLane = new Map();
    for (const lane of lanes) byLane.set(lane.id, { total: 0, steps: [] });

    let attributed = 0;
    let unattributed = 0;

    for (let t = 0; t < safeTurns.length; t++) {
        const blocks = safeTurns[t]?.blocks || [];
        if (!blocks.length) continue;
        const role = safeTurns[t]?.role;
        // Characters first, tokens second: a turn's mass is converted once, at
        // the turn's own grain, and lanes take shares of it. Rounding each
        // block to whole tokens instead would let a turn's lane shares drift
        // off the estimate the same turns produce anywhere else.
        const charsByLane = new Map();
        let turnChars = 0;
        let loose = 0;
        for (let b = 0; b < blocks.length; b++) {
            const block = blocks[b];
            const chars = blockChars(block);
            if (chars <= 0) continue;
            turnChars += chars;
            let owners = null;
            if (block.type === "tool_use") {
                owners = byPos.get(`${t}:${b}`) || null;
            } else if (block.type === "tool_result") {
                owners = block.tool_use_id ? byUseId.get(block.tool_use_id) || null : null;
            } else {
                const id = roleLane(role, t);
                if (id) owners = [id];
            }
            if (!owners) {
                loose += chars;
                continue;
            }
            const size = owners.size ?? owners.length;
            const each = chars / size;
            for (const laneId of owners) charsByLane.set(laneId, (charsByLane.get(laneId) || 0) + each);
        }
        if (!(turnChars > 0)) continue;

        const est = estimateTurnTokens(safeTurns[t]);
        unattributed += est * (loose / turnChars);
        for (const [laneId, chars] of charsByLane) {
            const w = byLane.get(laneId);
            if (!w) continue;
            const stepEst = est * (chars / turnChars);
            attributed += stepEst;
            w.total += stepEst;
            w.steps.push({ turn: t, est: stepEst, cumEst: w.total });
        }
    }

    let maxTotal = 0;
    for (const w of byLane.values()) if (w.total > maxTotal) maxTotal = w.total;
    if (!(maxTotal > 0)) return null;

    return { byLane, maxTotal, attributed, unattributed, whole: attributed + unattributed };
}

/**
 * Several lanes read as one row — a folded directory or group keeps its weight
 * the way it keeps its marks. Steps are re-accumulated over the union so the
 * merged run-up is the subtree's own, not a sum of run-ups.
 */
export function mergeLaneWeights(weights, laneIds) {
    if (!weights || !laneIds?.length) return null;
    const perTurn = new Map();
    for (const id of laneIds) {
        const w = weights.byLane.get(id);
        if (!w) continue;
        for (const step of w.steps) perTurn.set(step.turn, (perTurn.get(step.turn) || 0) + step.est);
    }
    if (!perTurn.size) return null;
    let total = 0;
    const steps = [...perTurn.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([turn, est]) => {
            total += est;
            return { turn, est, cumEst: total };
        });
    return { total, steps };
}
